import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// SoftUI/MUI Componentns
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";
import { useTheme, Grid, Tabs, Tab, Popover, TextField, Modal, Backdrop, Fade } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Components
// import PageHeader from "../../components/PageHeader";
import LoadingScreen from "../../components/LoadingScreen";
import Notes from "../../components/Notes";
import SearchBar from "../../components/SearchBar";
import TimelineList from "../../components/Timeline/TimelineList";
import TimelineItem from "../../components/Timeline/TimelineItem";
import popNotification from "../../components/Modals/popNotification";
import ItemBox from "../../components/YetiKpiBox/ItemBox";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import SoftSelect from "../../components/SoftSelect";

// API Services
import { GET_USER_KPIS, UPDATE_USER_KPI, DELETE_USER_KPI } from "../../api/kpi";

// Hooks
import { UseCompanyId, UseUserId, UseIsAdmin } from "../../hooks/auth";
import { UseSelectedWorkspace, UseUsersDictionary } from "../../hooks/company";
import { UseGroupKpis, UseGroupAllKpis, UseCompanyKpis, UseMyKpis, UseKpiScoresTable, useKpiUpdateScore, useTotalScoreSave, UseTopPlayers, isExcludedFromBoards } from "../../hooks/kpi";

// Redux
import { getPreviousKpis, refreshKpis } from "../../redux/kpi/actions";

// Utils
import {
  companyDayKey,
  companyDayKeyFromPicker,
  companyToday,
  companyTodayStr,
  formatCompanyDate,
  formatCompanyTime,
} from "../../helpers/companyTime";
import { resolveMonthKpiKey } from "../../constants/kpi";
import { formatNumber } from "../../helpers/formatter";
import {
  applyTimelineJobFields,
  buildIntegratedDescriptionParts,
  buildIntegratedParamFields,
  buildManualFormFields,
  findKpiInCatalog,
  formatJobLeadName,
  formatScoreChangeDescription,
  formatTimelineMetricTitle,
  formatIntegratedRefSubtitle,
  formatReviewRatingBadge,
  getMembershipPlanName,
  getTimelineRefId,
  getTimelineRefModel,
  isIntegratedTimelineEntry,
  isIntegratedJobEntry,
  isManualTimelineEntry,
  resolveJobTag,
  resolveKpiDisplayName,
} from "../../helpers/timelineEntry";

// Icons
import { ReactComponent as AdminIcon } from "../../img/timeline-adminicon.svg";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { ReactComponent as IntegratedIcon } from "../../img/timeline-integratedicon.svg";
import { ReactComponent as ManualIcon } from "../../img/timeline-manualicon.svg";
import { ReactComponent as DeletedIcon } from "../../img/timeline-deletedicon.svg";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";

// import boxShadow from "../../assets/theme/functions/boxShadow";
// import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
// import GroupIcon from "@mui/icons-material/Group";
import useMediaQuery from "@mui/material/useMediaQuery";
// import SoftBoxRoot from "../../components/SoftBox/SoftBoxRoot";

const HIDDEN_INTEGRATION_LABEL =
  process.env.REACT_APP_INTEGRATION_LABEL === "true";

// Quick presets for the timeline's date filter. `days: null` means "All
// time". Changing the preset re-queries the server with the matching window.
const DATE_PRESETS = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 },
  { key: "all", label: "All time", days: null },
];
const DEFAULT_DATE_PRESET = "90d";
const TIMELINE_PAGE_SIZE = 50;

const KPI_SOURCE = {
  INTEGRATED: {
    icon: <IntegratedIcon />,
    description: "Integrated score",
  },
  ADMIN_ADJUSTED: {
    icon: <AdminIcon />,
    description: "Admin-adjusted reported score",
  },
  ADMIN_ENTERED: {
    icon: <AdminPanelSettingsIcon />,
    description: "Admin-entered reported score",
  },
  PLAYER_ENTERED: {
    icon: <ManualIcon />,
    description: "Player-entered reported score",
  },
  DELETED: {
    icon: <DeletedIcon />,
    description: "Deleted Score",
  },
  CLAIMED: {
    icon: <IntegratedIcon />,
    description: "Claimed a review",
  },
  UNCLAIMED: {
    icon: <DeletedIcon />,
    description: "Unclaimed a review",
  },
  OTHER: {
    icon: <AdminIcon />,
    description: "Other reported score",
  },
};

const Timeline = () => {
  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;
  const companyId = UseCompanyId();
  const currentWorkspace = UseSelectedWorkspace();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdParam = searchParams.get("user");
  const kpiIdParam = searchParams.get("kpi");
  const playerMode = searchParams.get("player");
  const isAdmin = UseIsAdmin();
  const groupKpis = UseGroupKpis();
  const groupAllKpis = UseGroupAllKpis();
  const companyKpis = UseCompanyKpis();
  const draftKpis = useSelector((state) => state.kpiReducer.draftKpis);
  const kpiCatalogSources = useMemo(
    () => [groupKpis, groupAllKpis, draftKpis, companyKpis],
    [groupKpis, groupAllKpis, draftKpis, companyKpis],
  );
  const usersDict = UseUsersDictionary();
  const [userId, setUserId] = useState(userIdParam);
  const [kpiId, setKpiId] = useState(kpiIdParam);
  const [noteKpiId, setNoteKpiId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);
  const [allKpis, setAllKpis] = useState([]);
  const [message, setMessage] = useState(null);
  const [monthRef, setMonthRef] = useState(null);
  const userKpis = UseMyKpis();
  const [isSelected, setIsSelected] = useState(null);
  const [player, setPlayer] = useState(null);
  const isSmallScreen = useMediaQuery((themes) => themes.breakpoints.down("md"));
  const [playerSelectOpen, setPlayerSelectOpen] = useState(false);
  const [showAll, setShowAll] = useState(1);
  const [hasMoreTimeline, setHasMoreTimeline] = useState(false);
  const [timelineCursor, setTimelineCursor] = useState(null);
  const [loadingMoreTimeline, setLoadingMoreTimeline] = useState(false);
  // "Last 30 days" timeline date filter â€” see DATE_PRESETS above.
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [datePreset, setDatePreset] = useState(DEFAULT_DATE_PRESET);
  const [customRangeStart, setCustomRangeStart] = useState(null);
  const [customRangeEnd, setCustomRangeEnd] = useState(null);
  const playerId = UseUserId();
  const topPlayers = UseTopPlayers();
  const scorekeeperPlayers = UseKpiScoresTable(null, currentWorkspace?._id);
  const workspaceUsersKpis = useSelector((state) => state.kpiReducer.usersKpis);
  const playerData = topPlayers?.find(
    (dplayer) => String(dplayer.userId) === String(userId),
  );
  const screenWidth = window.innerWidth;
  const isPlayerScorecard = Boolean(userIdParam || playerMode);

  const dateRange = useMemo(() => {
    if (datePreset === "custom") {
      if (!customRangeStart && !customRangeEnd) return null;
      return {
        startDay: companyDayKeyFromPicker(customRangeStart),
        endDay: companyDayKeyFromPicker(customRangeEnd),
      };
    }
    const preset = DATE_PRESETS.find((p) => p.key === datePreset);
    if (!preset || preset.days == null) return null; // "All time" â€” no bound
    return {
      startDay: companyToday()
        .clone()
        .subtract(preset.days - 1, "days")
        .format("YYYY-MM-DD"),
      endDay: companyTodayStr(),
    };
  }, [datePreset, customRangeStart, customRangeEnd]);

  const timelineFetchParams = useMemo(() => {
    if (datePreset === "custom") {
      const startDate = companyDayKeyFromPicker(customRangeStart);
      const endDate = companyDayKeyFromPicker(customRangeEnd);
      if (startDate || endDate) return { startDate, endDate };
      return { days: "all" };
    }
    const preset = DATE_PRESETS.find((p) => p.key === datePreset);
    if (!preset || preset.days == null) return { days: "all" };
    return { days: preset.days };
  }, [datePreset, customRangeStart, customRangeEnd]);

  const dateRangeLabel = useMemo(() => {
    if (datePreset === "custom") {
      if (customRangeStart && customRangeEnd) {
        return `${formatCompanyDate(customRangeStart, { year: "numeric" })} - ${formatCompanyDate(customRangeEnd, { year: "numeric" })}`;
      }
      return "Custom range";
    }
    return DATE_PRESETS.find((p) => p.key === datePreset)?.label ?? "Last 30 days";
  }, [datePreset, customRangeStart, customRangeEnd]);

  // SearchBar spreads onClick onto its inner input only â€” the calendar icon
  // sits in a sibling box beside it â€” so event.currentTarget was narrower
  // than the visible field and the popover ended up anchored short of the
  // icon, leaving a gap on the right. Anchor to the full field wrapper
  // instead so the popover's right edge lines up with the field's.
  const dateFieldRef = useRef(null);
  const handleOpenDateFilter = () => setDateAnchorEl(dateFieldRef.current);
  const handleCloseDateFilter = () => setDateAnchorEl(null);
  const handleSelectDatePreset = (key) => {
    setDatePreset(key);
    if (key !== "custom") handleCloseDateFilter();
  };

  const selectedPlayerScores = useMemo(() => {
    if (!userId || !Array.isArray(workspaceUsersKpis)) return null;
    const row = workspaceUsersKpis.find(
      (u) => String(u.userId) === String(userId),
    );
    return row?.month ?? null;
  }, [workspaceUsersKpis, userId]);

  const kpis = useMemo(() => {
    if (!groupKpis?.length) return [];

    let dataSource;
    if (isPlayerScorecard && userId) {
      dataSource = selectedPlayerScores;
    } else if (kpiIdParam) {
      dataSource = userKpis?.month;
    } else {
      dataSource = playerData?.scores;
    }

    const kpiSummary = groupKpis
      /* Rollup metrics (Total Revenue, Total Scheduled Revenue, Team Hours)
         are company/workspace-wide numbers with no player behind them —
         they were showing up as if they were this person's own tiles.
         Same exclusion Past Boards/TV already apply. */
      .filter((kpi) => !kpi.isDraft && !isExcludedFromBoards(kpi, monthRef))
      .map((kpiItem) => {
        const scoreKey = resolveMonthKpiKey(dataSource, kpiItem._id);
        const amount = dataSource?.[scoreKey]?.amt ?? 0;
        const ptsScale = Number(kpiItem.pointScale) || 0;
        const ptsValue = Number(kpiItem.pointValue) || 0;
        const ptsPerAmt = ptsScale > 0 ? ptsValue / ptsScale : ptsValue;
        return {
          ...kpiItem,
          amount,
          points: amount * ptsPerAmt,
          target: kpiItem.target,
        };
      });

    return kpiSummary.sort((a, b) => b.amount - a.amount);
  }, [
    groupKpis,
    isPlayerScorecard,
    userId,
    selectedPlayerScores,
    userKpis,
    kpiIdParam,
    playerData,
    monthRef,
  ]);

  const workspacePlayerOptions = useMemo(() => {
    if (!scorekeeperPlayers?.length) return [];
    return scorekeeperPlayers
      .filter((row) => row.info && row.info.lastName !== "Yeti")
      .map((row) => ({
        value: row.userId,
        label: `${row.info.firstName} ${row.info.lastName}`.trim(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [scorekeeperPlayers]);

  const selectedPlayerOption = useMemo(
    () => workspacePlayerOptions.find((opt) => opt.value === userId) ?? null,
    [workspacePlayerOptions, userId],
  );

  const handlePlayerChange = useCallback(
    (option) => {
      if (!option?.value) return;
      setUserId(option.value);
      setIsSelected(null);
      setKpiId(null);
      setAllKpis([]);
      setMessage(null);
      const next = new URLSearchParams(searchParams);
      next.set("user", option.value);
      next.delete("player");
      next.delete("kpi");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (userIdParam) {
      setUserId(userIdParam);
    }
  }, [userIdParam]);

  useEffect(() => {
    if (playerMode && playerId && !userIdParam) {
      setUserId(playerId);
    }
  }, [playerMode, playerId, userIdParam]);

  useEffect(() => {
    if (userId && usersDict) {
      setPlayer({ ...usersDict[userId], userId });
    }
  }, [userId, usersDict]);

  useEffect(() => {
    if (kpiIdParam) {
      setIsSelected(kpiIdParam);
      setKpiId(kpiIdParam);
    } else if (playerMode || userIdParam) {
      // Player scorecard: show all entries on open; filter only after metric click
      setIsSelected(null);
      setKpiId(null);
    } else if (kpis?.length) {
      setIsSelected(kpis[0]?._id);
    }
  }, [kpis, kpiIdParam, userIdParam, playerMode]);

  useEffect(() => {
    if (monthRef) {
      dispatch(getPreviousKpis(monthRef));
    }
  }, [monthRef, dispatch, currentWorkspace]);

  useEffect(() => {
    if (!kpiIdParam && !playerMode && !userIdParam && groupKpis?.length) {
      setKpiId(groupKpis[0]._id);
    }
  }, [groupKpis, kpiIdParam, playerMode, userIdParam]);

  const formatItemDescription = (amount, kpiKind, kpiName = "", playerName = "") =>
    formatScoreChangeDescription(amount, kpiKind, kpiName, playerName);

  const enrichTimelineEntries = useCallback((eKpis) => {
    const entries = eKpis.map((ekpi) => ({ ...ekpi }));

    entries.forEach((ekpi) => {
      const users = usersDict[ekpi.ownerId];
      ekpi.name = users?.firstName
        ? `${users.firstName} ${users.lastName}`
        : "Unregistered";

      // Resolve who created a manual entry (reporterId is only meant to be set
      // for player/admin-entered scores; integrated entries have none). Some
      // legacy/misattributed integration syncs still carry a reporterId, so
      // guard against them to avoid a bogus "Added by". Note manual entries
      // (e.g. Yard Sign) legitimately have a UUID entryId + photo/address
      // metadata, so entryId alone is NOT an integration signal â€” only the
      // specific integration markers below are: HCP dedup keys (job_/est_),
      // a Job/Estimate link, or system-computed metadata (source/formula/
      // jobAmount).
      const entryIdStr = String(ekpi.entryId ?? "");
      const isIntegrationGenerated =
        /^(job_|est_)/.test(entryIdStr) ||
        ["Job", "Estimate"].includes(ekpi.refModel) ||
        Boolean(ekpi.metadata?.source) ||
        Boolean(ekpi.metadata?.formula) ||
        ekpi.metadata?.jobAmount !== undefined;
      const reporter = isIntegrationGenerated ? null : usersDict[ekpi.reporterId];
      ekpi.reporterName = reporter?.firstName
        ? `${reporter.firstName} ${reporter.lastName ?? ""}`.trim()
        : null;
    });

    entries.forEach((ekpi, index) => {
      const obj =
        findKpiInCatalog(ekpi.kpiId, kpiCatalogSources) ||
        groupKpis?.find((kpi) => String(kpi._id) === String(ekpi.kpiId));

      const serverScoreType = ekpi.scoreType;
      ekpi.kpiName = resolveKpiDisplayName(obj) || ekpi.kpiName;
      ekpi.kind = obj?.kind ?? ekpi.kind;
      ekpi.scoreType = "OTHER";
      ekpi.source = obj?.source?.toUpperCase() ?? ekpi.source ?? "NONE";
      ekpi.metricType = obj?.metricType ?? ekpi.metricType;
      ekpi._kpiDef = obj;

      if (index === 0 && obj?.metricType === "anchor") {
        ekpi.currentValue = true;
      }

      Object.assign(ekpi, applyTimelineJobFields(ekpi));

      const isOffset = ekpi?.isOffset ?? false;

      if (ekpi?.metadata?.deleted) {
        // A deleted entry should always read as a "Deleted Score" tombstone,
        // regardless of the underlying metric type (composite, hours
        // adjustment, etc.) — checked first so those branches below can't
        // shadow it and hide the deletion.
        ekpi.scoreType = "DELETED";
        ekpi.deletedAt = ekpi?.metadata?.deletedAt;
      } else if (serverScoreType === "CLAIMED" || serverScoreType === "UNCLAIMED") {
        ekpi.scoreType = serverScoreType;
      } else if (ekpi?.metadata?.isComposite) {
        // Human edited a source metric (A/B) â†’ shield; system/HCP recompute â†’ lightning
        ekpi.scoreType = ekpi.reporterId ? "ADMIN_ENTERED" : "INTEGRATED";
      } else if (ekpi?.metadata?.type === "hours_adjustment") {
        // Hours Worked: lightning unless an admin modified it
        if (
          ekpi.reporterId &&
          String(ekpi.reporterId) !== String(ekpi.ownerId)
        ) {
          ekpi.scoreType = "ADMIN_ENTERED";
        } else {
          ekpi.scoreType = "INTEGRATED";
        }
      } else if (ekpi.reporterId === undefined || ekpi.reporterId === null) {
        ekpi.scoreType = "INTEGRATED";
      } else if (isOffset) {
        ekpi.scoreType = "ADMIN_ADJUSTED";
      } else if (ekpi?.reporterId !== ekpi?.ownerId) {
        ekpi.scoreType = "ADMIN_ENTERED";
      } else if (ekpi?.reporterId === ekpi?.ownerId) {
        ekpi.scoreType = "PLAYER_ENTERED";
      }
    });

    return entries;
  }, [usersDict, kpiCatalogSources, groupKpis]);

  const getTimelineItemProps = useCallback((timeline) => {
    const kpiDef =
      timeline._kpiDef || findKpiInCatalog(timeline.kpiId, kpiCatalogSources);
    const kpiName = timeline.kpiName || resolveKpiDisplayName(kpiDef);

    if (timeline.scoreType === "DELETED") {
      return {
        variant: "default",
        title: KPI_SOURCE.DELETED.description,
        description: formatItemDescription(
          timeline.amount,
          timeline.kind,
          kpiName,
          timeline.name,
        ),
      };
    }

    // Hours / Composite: same layout as other metrics (title + description),
    // only the copy differs. Hide redundant "Added by".
    // Composite: source-metric line first, then author delta line.
    if (timeline.metadata?.isComposite) {
      const { kind, amount: compositeAmount } = timeline;
      const {
        previousMonthTotal: prevTotal,
        monthTotal: newTotal,
        sourceKpiName,
      } = timeline.metadata || {};
      const verb = Number(compositeAmount) >= 0 ? "added" : "deducted";
      const deltaFormatted = formatNumber(
        Math.abs(Number(compositeAmount) || 0),
        kind,
      );
      const fromValue = formatNumber(Number(prevTotal != null ? prevTotal : 0), kind);
      const toValue = formatNumber(Number(newTotal != null ? newTotal : 0), kind);
      const author =
        timeline.reporterName || timeline.name || "Someone";
      const fromTo =
        prevTotal != null || newTotal != null
          ? `, from ${fromValue} to ${toValue}`
          : "";
      const authorLine = `${author} ${verb} ${deltaFormatted}${fromTo}.`;
      const description = sourceKpiName
        ? `${sourceKpiName} has been modified.\n${authorLine}`
        : authorLine;
      return {
        variant: "default",
        title: formatTimelineMetricTitle(kpiName, compositeAmount, kind),
        description,
        hideAddedBy: true,
      };
    }

    if (timeline.metadata?.type === "hours_adjustment") {
      const { kind: timelineKind, amount: adjAmount } = timeline;
      const kind = timelineKind || "#";
      const verb = Number(adjAmount) >= 0 ? "added" : "deducted";
      const { previousAmount: prev, newAmount: next } = timeline.metadata || {};
      const deltaFormatted = formatNumber(Math.abs(Number(adjAmount) || 0), kind);
      const author =
        timeline.reporterName || timeline.name || "Someone";
      const fromTo =
        prev != null && next != null
          ? `, from ${formatNumber(Number(prev), kind)} to ${formatNumber(Number(next), kind)}`
          : "";
      return {
        variant: "default",
        title: formatTimelineMetricTitle(kpiName, adjAmount, kind),
        description: `${author} ${verb} ${deltaFormatted}${fromTo}.`,
        hideAddedBy: true,
      };
    }

    if (
      timeline.scoreType === "CLAIMED" ||
      timeline.scoreType === "UNCLAIMED"
    ) {
      const refModel = getTimelineRefModel(timeline) || "Review";
      const refId = getTimelineRefId(timeline);
      return {
        variant: "integrated-ref",
        refModel,
        refId,
        title: KPI_SOURCE[timeline.scoreType].description,
        subtitle1: formatIntegratedRefSubtitle(timeline.metadata),
        technicianName: timeline.name,
        refBadge:
          refModel === "Review"
            ? formatReviewRatingBadge(timeline.metadata?.rating)
            : null,
        descriptionParts: null,
        formFields: [],
      };
    }

    if (isIntegratedJobEntry(timeline)) {
      return {
        variant: "integrated-job",
        title: formatTimelineMetricTitle(kpiName, timeline.amount, timeline.kind),
        subtitle1: formatJobLeadName(timeline.metadata),
        technicianName: timeline.metadata?.technicianName || timeline.name,
        jobTag: resolveJobTag(timeline, null),
        descriptionParts: buildIntegratedDescriptionParts(
          timeline.amount,
          timeline.kind,
          kpiName,
          timeline.name,
        ),
        formFields: buildIntegratedParamFields(timeline.metadata, {
          formatScores: Boolean(kpiDef?.parameters?.formKpiId),
          kpiMeta: kpiDef,
        }),
      };
    }

    if (isIntegratedTimelineEntry(timeline)) {
      const refModel = getTimelineRefModel(timeline);
      const refId = getTimelineRefId(timeline);

      return {
        variant: "integrated-ref",
        refModel,
        refId,
        title: formatTimelineMetricTitle(kpiName, timeline.amount, timeline.kind),
        subtitle1: formatIntegratedRefSubtitle(timeline.metadata),
        technicianName: timeline.name,
        // Reviews badge their star rating; Memberships badge their plan name
        // (Clean 365, VIP, ...) so the plan reads directly off the timeline.
        refBadge:
          refModel === "Review"
            ? formatReviewRatingBadge(timeline.metadata?.rating)
            : getMembershipPlanName(timeline.metadata),
        descriptionParts: buildIntegratedDescriptionParts(
          timeline.amount,
          timeline.kind,
          kpiName,
          timeline.name,
        ),
        formFields: buildIntegratedParamFields(timeline.metadata, {
          formatScores: Boolean(kpiDef?.parameters?.formKpiId),
          kpiMeta: kpiDef,
        }),
      };
    }

    if (isManualTimelineEntry(timeline.scoreType)) {
      const formFields = buildManualFormFields(timeline, kpiDef);
      return {
        variant: "manual",
        title: formatTimelineMetricTitle(kpiName, timeline.amount, timeline.kind),
        formFields,
        description: "",
      };
    }

    return {
      variant: "default",
      title:
        KPI_SOURCE[timeline?.scoreType]?.description ??
        KPI_SOURCE.OTHER.description,
      description: formatItemDescription(
        timeline.amount,
        timeline.kind,
        kpiName,
        timeline.name,
      ),
    };
  }, [kpiCatalogSources]);

  const enrichTimelineEntriesRef = useRef(enrichTimelineEntries);
  useEffect(() => {
    enrichTimelineEntriesRef.current = enrichTimelineEntries;
  });

  const parseTimelineResponse = (body) => {
    if (Array.isArray(body)) {
      return { items: body, hasMore: false, nextCursor: null };
    }
    return {
      items: body?.items || [],
      hasMore: Boolean(body?.hasMore),
      nextCursor: body?.nextCursor || null,
    };
  };

  useEffect(() => {
    if (kpiId || userId) {
      setIsLoading(true);
      setHasMoreTimeline(false);
      setTimelineCursor(null);
      GET_USER_KPIS(companyId, userId, kpiId, {
        ...timelineFetchParams,
        limit: TIMELINE_PAGE_SIZE,
      })
        .then(({ data }) => {
          const { items, hasMore, nextCursor } = parseTimelineResponse(data);
          setIsLoading(false);
          if (items.length === 0) {
            setMessage("No recent records found");
            setAllKpis([]);
          } else {
            setMessage(null);
            setAllKpis(enrichTimelineEntriesRef.current(items));
          }
          setHasMoreTimeline(hasMore);
          setTimelineCursor(nextCursor);
        })
        .catch((err) => {
          setIsLoading(false);
          setAllKpis([]);
          setMessage(err.message);
        });
    }
  }, [companyId, kpiId, userIdParam, userId, timelineFetchParams]);

  const handleLoadMoreTimeline = useCallback(() => {
    if (!hasMoreTimeline || loadingMoreTimeline || !timelineCursor) return;
    setLoadingMoreTimeline(true);
    GET_USER_KPIS(companyId, userId, kpiId, {
      ...timelineFetchParams,
      limit: TIMELINE_PAGE_SIZE,
      before: timelineCursor,
    })
      .then(({ data }) => {
        const { items, hasMore, nextCursor } = parseTimelineResponse(data);
        if (items.length) {
          setAllKpis((prev) => [
            ...prev,
            ...enrichTimelineEntriesRef.current(items),
          ]);
        }
        setHasMoreTimeline(hasMore);
        setTimelineCursor(nextCursor);
      })
      .catch((err) => {
        popNotification({
          title: "Error",
          text: err.message || "Could not load more timeline entries",
          icon: "error",
        });
      })
      .finally(() => setLoadingMoreTimeline(false));
  }, [
    companyId,
    userId,
    kpiId,
    timelineFetchParams,
    hasMoreTimeline,
    loadingMoreTimeline,
    timelineCursor,
  ]);

  useEffect(() => {
    if (!allKpis.length) return;
    setAllKpis((prev) => enrichTimelineEntries(prev));
  }, [groupAllKpis, companyKpis, draftKpis, groupKpis, enrichTimelineEntries]);

  const handleOpenNotes = (employeeKpiId) => {
    setNoteKpiId(employeeKpiId);
    setOpenNotes(true);
  };

  const handleCloseNotes = () => {
    setOpenNotes(false);
  };

  const handleNoteCount = (id) => {
    const eKpis = [...allKpis];
    const kpiIndex = eKpis.findIndex((item) => item._id === id);

    if (kpiIndex === -1) return;

    eKpis[kpiIndex].noteCount += 1;
    setAllKpis(eKpis);
  };

  const updateLocalKpiState = useCallback(({
    id,
    amount = 0,
    points = 0,
    operation = "edit",
  }) => {
    // Local state update after POST/DELETE success
    const tempKpis = [...allKpis];
    const recordIndex = tempKpis.findIndex((x) => x._id === id);

    if (recordIndex === -1) return;

    if (operation === "edit") {
      tempKpis[recordIndex].amount = amount;
      tempKpis[recordIndex].points = points;
    } else {
      // Delete: keep the row but flag it as deleted so it renders as a
      // "Deleted Score" tombstone (matching what the server actually stores)
      // instead of the entry just vanishing from the timeline. Only
      // scoreType/deletedAt/metadata.deleted matter for that render path —
      // leave everything else (kpiName, currentValue, ...) untouched.
      const deletedAt = new Date().toISOString();
      tempKpis[recordIndex] = {
        ...tempKpis[recordIndex],
        scoreType: "DELETED",
        deletedAt,
        metadata: {
          ...(tempKpis[recordIndex].metadata || {}),
          deleted: true,
          deletedAt,
        },
      };
    }

    setAllKpis(tempKpis);
  }, [allKpis]);

  const { updateKpiScore } = useKpiUpdateScore();
  const { saveTotalScore } = useTotalScoreSave();

  const handleUpdateKpi = useCallback(async (id, eKpiId, value, amount, points, ownerId, currentValue) => {
    try {
      setIsLoading(true);
      if (currentValue) {
        saveTotalScore(eKpiId, ownerId, value);
      } else {
        const response = await UPDATE_USER_KPI(id, eKpiId, value, ownerId);
        const oldAmount = amount;
        const oldPoints = points;
        const newAmount = response?.data?.amount ?? 0;
        const newPoints = response?.data?.points ?? 0;
        updateKpiScore(eKpiId, response?.data?.ownerId, oldAmount, oldPoints, newAmount, newPoints);
      }
      popNotification({
        title: "Success",
        text: "The KPI entry was updated.",
      });
    } catch (err) {
      popNotification({
        title: "Error",
        text: `The KPI entry was not updated\n${err.message}`,
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [updateKpiScore, saveTotalScore]);

  const handleDeleteKpi = useCallback(async (id) => {
    try {
      setIsLoading(true);
      await DELETE_USER_KPI(id);

      dispatch(refreshKpis());
      updateLocalKpiState({ id, operation: "delete" });

      popNotification({
        title: "Success",
        text: "The KPI entry was deleted.",
      });
    } catch (err) {
      popNotification({
        title: "Error",
        text: `The KPI entry was not deleted\n${err.message}`,
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, updateLocalKpiState]);

  const visibleTimelineEntries = useMemo(() => {
    return allKpis
      .filter((timeline) => !isSelected || timeline.kpiId === isSelected)
      .filter((timeline) => {
        if (!dateRange) return true; // "All time"
        const day = companyDayKey(timeline.createdAt);
        if (dateRange.startDay && day < dateRange.startDay) return false;
        if (dateRange.endDay && day > dateRange.endDay) return false;
        return true;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [allKpis, isSelected, dateRange]);

  const timelineListContent = useMemo(
    () =>
      // eslint-disable-next-line no-nested-ternary
      isLoading ? (
        <LoadingScreen />
      ) : visibleTimelineEntries.length > 0 ? (
        <SoftBox width="100%">
          <TimelineList>
            {visibleTimelineEntries.map((timeline) => {
            const itemProps = getTimelineItemProps(timeline);
            return (
            <TimelineItem
              key={timeline._id}
              color="success"
              icon={
                KPI_SOURCE[timeline?.scoreType]?.icon ??
                KPI_SOURCE.OTHER.icon
              }
              eKpidId={timeline._id}
              notesText={`${timeline?.noteCount ?? 0}`}
              title={itemProps.title}
              date={formatCompanyDate(timeline.createdAt, { day: "2-digit" })}
              description={itemProps.description ?? ""}
              hideAddedBy={itemProps.hideAddedBy}
              variant={itemProps.variant}
              subtitle1={itemProps.subtitle1}
              technicianName={itemProps.technicianName}
              jobTag={itemProps.jobTag}
              refModel={itemProps.refModel}
              refId={itemProps.refId}
              refBadge={itemProps.refBadge}
              formFields={itemProps.formFields}
              descriptionParts={itemProps.descriptionParts}
              time={formatCompanyTime(timeline.createdAt)}
              timeline={timeline}
              handleOpenNotes={handleOpenNotes}
              handleKpiUpdate={handleUpdateKpi}
              handleKpiDelete={handleDeleteKpi}
              deletedAt={timeline?.deletedAt}
            />
            );
          })}
          </TimelineList>
          <SoftBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            py={2}
          >
            <SoftTypography variant="caption" color="secondary">
              Showing {visibleTimelineEntries.length} entries
            </SoftTypography>
            {hasMoreTimeline && (
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={handleLoadMoreTimeline}
                disabled={loadingMoreTimeline}
              >
                {loadingMoreTimeline ? "Loading…" : "Load more"}
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>
      ) : (
        <SoftBox padding={2} width="100%">
          <SoftTypography
            variant="h6"
            color="primary"
            sx={{ textAlign: "center", width: "100%" }}
          >
            {allKpis.length > 0 ? "No entries in this date range" : "No data"}
          </SoftTypography>
        </SoftBox>
      ),
    [isLoading, allKpis, visibleTimelineEntries, handleUpdateKpi, handleDeleteKpi, getTimelineItemProps, hasMoreTimeline, loadingMoreTimeline, handleLoadMoreTimeline],
  );

  return (
    <>
      <SoftBox sx={{ marginTop: { mini: "0.75rem", sm: "1.5rem" }, marginBottom: "0.5rem" }}>
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { mini: "nowrap", md: "wrap" },
            gap: { mini: "8px", md: "1rem" },
            padding: {
              mini: "0 1rem",
              md: "0 1.5rem",
            },
          }}
        >
          <SoftBox sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: "1 1 auto" }}>
            {/* Mobile-only back chevron, beside the title â€” desktop already
                has one in the navbar breadcrumbs (same convention as
                PageHeader's inlineBack). */}
            <SoftBox
              component="button"
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              sx={{
                display: { mini: "flex", md: "none" },
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                padding: 0,
                marginLeft: 0,
                marginRight: "4px",
                borderRadius: "50%",
                border: 0,
                color: "#343A40",
                background: "rgba(15,23,42,0.04)",
                cursor: "pointer",
              }}
            >
              <KeyboardArrowLeftRoundedIcon sx={{ fontSize: "1.5rem" }} />
            </SoftBox>
            <SoftTypography variant="h4" sx={{
              fontWeight: 600,
              fontSize: { mini: "15px", md: "24.6071px" },
              lineHeight: "32px",
              color: "#000000",
              whiteSpace: "nowrap",
            }}>
              {userIdParam || playerMode ? "Player's Scorecard" : "Teams Scorecard"}
            </SoftTypography>
          </SoftBox>
          <SoftBox ref={dateFieldRef} sx={{ width: { mini: "136px", sm: "180px", md: "auto" }, flexShrink: 0 }}>
            {/* This used to be a plain SearchBar with a "Last 30 days"
                placeholder and a calendar icon â€” no click handler, no state,
                nothing actually behind it. It's now a real (client-side)
                date filter over the entries already loaded below. */}
            <SearchBar
              size={isSmallScreen ? "small" : "medium"}
              value={dateRangeLabel}
              readOnly
              onClick={handleOpenDateFilter}
              icon={{
                component: <EventOutlinedIcon sx={{ fill: "#6C757D" }} />,
                direction: "right",
              }}
              sx={{
                width: "100%",
                fontSize: { mini: "12px", md: "14px" },
                cursor: "pointer",
                caretColor: "transparent",
                "&.Mui-focused": {
                  borderColor: primaryColor,
                  boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                  outline: 0,
                },
              }}
            />
            <Popover
              open={Boolean(dateAnchorEl)}
              anchorEl={dateAnchorEl}
              onClose={handleCloseDateFilter}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  // The app-wide MuiPopover theme override (assets/theme/
                  // components/popover.js) makes every popover paper
                  // transparent by default â€” menus elsewhere wrap their own
                  // opaque SoftBox, but this one didn't, so the page behind
                  // it (KPI cards, tabs) was showing straight through.
                  backgroundColor: "#FFFFFF !important",
                  borderRadius: "12px",
                  padding: "0.75rem",
                  width: "260px",
                  maxWidth: "calc(100vw - 32px)",
                  boxShadow: "0px 12px 32px -8px rgba(16,24,40,0.16)",
                  marginTop: "0.5rem",
                },
              }}
            >
              <SoftTypography
                variant="caption"
                sx={{ fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: "0.02em", paddingLeft: "0.25rem" }}
              >
                Date range
              </SoftTypography>
              <SoftBox sx={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginTop: "0.5rem" }}>
                {DATE_PRESETS.map((preset) => (
                  <SoftButton
                    key={preset.key}
                    variant={datePreset === preset.key ? "contained" : "text"}
                    color="primary"
                    onClick={() => handleSelectDatePreset(preset.key)}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontSize: "14px",
                      boxShadow: "none !important",
                      padding: "0.4rem 0.6rem",
                    }}
                  >
                    {preset.label}
                  </SoftButton>
                ))}
              </SoftBox>
              <SoftBox
                sx={{
                  marginTop: "0.5rem",
                  paddingTop: "0.5rem",
                  borderTop: "1px solid #E9ECEF",
                }}
              >
                <SoftTypography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: "0.02em", paddingLeft: "0.25rem" }}
                >
                  Custom range
                </SoftTypography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  {/* A shrunk outlined label straddles its own field's top
                      border and needs ~16-18px of clearance above the box
                      to avoid bleeding into whatever sits above it â€” 0.5rem
                      (8px) wasn't enough, so "To"'s label was overlapping
                      the "From" field below it. */}
                  <SoftBox sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", marginTop: "1.25rem" }}>
                    <DatePicker
                      label="From"
                      value={customRangeStart}
                      maxDate={customRangeEnd || undefined}
                      onChange={(newValue) => {
                        setCustomRangeStart(newValue);
                        setDatePreset("custom");
                      }}
                      // The picker's own calendar flyout is a separate
                      // Popper nested inside this Popover â€” give it a
                      // higher z-index so it draws above the Popover paper
                      // instead of underneath/behind it.
                      PopperProps={{ sx: { zIndex: 1400 } }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" fullWidth helperText={null} />
                      )}
                    />
                    <DatePicker
                      label="To"
                      value={customRangeEnd}
                      minDate={customRangeStart || undefined}
                      maxDate={moment()}
                      onChange={(newValue) => {
                        setCustomRangeEnd(newValue);
                        setDatePreset("custom");
                      }}
                      PopperProps={{ sx: { zIndex: 1400 } }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" fullWidth helperText={null} />
                      )}
                    />
                  </SoftBox>
                </LocalizationProvider>
                <SoftButton
                  variant="text"
                  color="primary"
                  onClick={handleCloseDateFilter}
                  sx={{ textTransform: "none", fontSize: "13px", marginTop: "0.5rem", boxShadow: "none !important" }}
                >
                  Done
                </SoftButton>
              </SoftBox>
            </Popover>
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <SoftBox
        sx={{
          overflowX: "auto",
          background: "white",
          borderRadius: "1rem",
          marginTop: { mini: "1rem", md: "2rem" },
          marginBottom: { mini: "1rem", md: "2rem" },
          marginX: { mini: "1rem", md: "1.5rem" },
        }}
      >
        <SoftBox
          sx={{
            padding: {
              mini: "1rem",
              md: "1.5rem",
            },
          }}
        > 
          <SoftBox
            sx={{
              borderRadius: "8px",
              marginBottom: "2rem",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              padding: {
                mini: "1rem",
                md: "1.5rem",
              },
            }}
          >
            <Grid container spacing={{ mini: 1, md: 2 }} sx={{ marginBottom: "1rem" }}>
              <Grid
                item
                mini={12}
                md={6}
              >
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                  >
                    <SoftProfile
                      src={isPlayerScorecard ? player?.image : currentWorkspace?.image}
                      name={isPlayerScorecard ? player?.firstName : currentWorkspace?.name}
                      size="lg"
                      fullCover
                    />
                    <SoftBox
                      width="100%"
                      sx={{
                        marginLeft: "1rem",
                      }}
                    >
                      <SoftTypography
                        component="h4"
                        variant="button"
                        textAlign="left"
                        sx={{ fontWeight: 600, fontSize: screenWidth <= 1280 ? "24px" : "36px", textTransform: "capitalize", color: "#344054", marginBottom: "-0.75rem", marginTop: "-0.5rem" }}
                      >
                        {isPlayerScorecard
                          ? [player?.firstName, player?.lastName].filter(Boolean).join(" ") || "\u2014"
                          : (currentWorkspace?.name ?? "")}
                      </SoftTypography>
                      {isPlayerScorecard && player?.group?.name ? (
                      <SoftTypography
                        variant="body2"
                        sx={{
                          color: "#667085",
                          fontSize: "16px",
                          fontWeight: 400,
                        }}
                        className="text-left truncate text-ellipsis"
                      >
                        {player.group.name}
                      </SoftTypography>
                      ) : null}
                    </SoftBox>
                  </SoftBox>
                  {isAdmin && isPlayerScorecard && workspacePlayerOptions.length > 0 && (
                    <SoftBox
                      sx={{
                        minWidth: { mini: "100%", sm: "220px" },
                        maxWidth: { sm: "280px" },
                        flex: { sm: "0 0 auto" },
                      }}
                    >
                      <SoftSelect
                        id="scorecard-player"
                        name="scorecard-player"
                        options={workspacePlayerOptions}
                        value={selectedPlayerOption}
                        onChange={handlePlayerChange}
                        onMenuOpen={() => setPlayerSelectOpen(true)}
                        onMenuClose={() => setPlayerSelectOpen(false)}
                        placeholder="Select player"
                        isSearchable
                        styles={{
                          control: {
                            backgroundColor: "#fff !important",
                            borderColor: "#D0D5DD",
                            minHeight: "44px",
                          },
                          placeholder: { color: "#98A2B3" },
                          singleValue: { color: "#344054" },
                          menu: {
                            zIndex: 40,
                            marginTop: "8px",
                            // Left/right split intentionally â€” the scrollbar
                            // already eats into the right inset, so an equal
                            // 8px both sides read as extra-wide on the left.
                            paddingTop: "8px",
                            paddingBottom: "8px",
                            paddingLeft: "4px",
                            paddingRight: "8px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #EAECF0",
                            borderRadius: "12px",
                            boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.12)",
                          },
                          menuList: {
                            maxHeight: "260px",
                            padding: 0,
                            // Native scrollbars run full-width on some browsers,
                            // eating far more space on the right than the 12px
                            // option padding matched on the left. A slim custom
                            // scrollbar keeps both sides visually even.
                            scrollbarWidth: "thin",
                            scrollbarColor: "#D0D5DD transparent",
                            "&::-webkit-scrollbar": {
                              width: "6px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "transparent",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#D0D5DD",
                              borderRadius: "3px",
                            },
                          },
                          option: {
                            minWidth: 0,
                            // top/right/bottom/left â€” left trimmed to match
                            // menu's own left/right split above.
                            padding: "10px 12px 10px 8px",
                            color: "#344054",
                            fontSize: "14px",
                            fontWeight: 500,
                            backgroundColor: "#FFFFFF",
                            borderRadius: "8px",
                            "&[aria-selected='true']": {
                              color: primaryColor,
                              backgroundColor: `${primaryColor}14`,
                            },
                            "&:hover, &:focus": {
                              color: "#101828",
                              backgroundColor: "#F2F4F7",
                            },
                          },
                        }}
                      />
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
              <Grid
                item
                mini={12}
                md={6}
                sx={{
                  display: { mini: playerSelectOpen ? "none" : "block", md: "block" },
                  marginTop: { mini: 0, md: "1.5rem" },
                }}
              >
                <SoftBox
                  sx={{
                    display: { mini: "grid", md: "flex" },
                    gridTemplateColumns: "minmax(0, 1fr) minmax(120px, 144px)",
                    columnGap: { mini: "0.5rem", md: 0 },
                    alignItems: "center",
                    justifyContent: "flex-start",
                    width: "100%",
                  }}
                >
                  <SoftTypography
                    variant="body2"
                    sx={{
                      width: { mini: "auto", md: "auto" },
                      flexShrink: 0,
                      color: "#667085",
                      fontSize: screenWidth <= 1280 ? "12px" : "18px",
                      fontWeight: 400,
                      textAlign: "left",
                      marginRight: { mini: 0, md: "2rem" },
                      position: "relative",
                      top: { mini: 0, md: "-0.5rem" },
                    }}
                  >
                    Viewing Timeline for
                  </SoftTypography>
                  <Tabs
                    value={showAll}
                    onChange={(e, value) => setShowAll(value)}
                    sx={{
                      marginTop: { mini: 0, md: "-1.25rem" },
                      backgroundColor: "#F9FAFB",
                      borderRadius: "8px",
                      padding: "5px",
                      width: "100%",
                      "& .MuiTabs-indicator": {
                        borderRadius: "6px",
                        fontSize: screenWidth <= 1280 ? "12px" : "16px",
                        background: primaryColor,
                        boxShadow: "0px 1.2px 3.6px rgba(16, 24, 40, 0.1), 0px 1.2px 2.4px rgba(16, 24, 40, 0.06)",
                        color: "#fff !important",
                      },
                      "& .MuiTab-root": {
                        padding: "5px 4px",
                        fontSize: screenWidth <= 1280 ? "12px" : "16px",
                        flex: 1,
                        minWidth: 0,
                        maxWidth: "none",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        "&.Mui-selected": {
                          color: "#fff !important",
                        },
                      },
                    }}
                  >
                    <Tab
                      label="All Metrics"
                      onClick={() => {
                        setKpiId(null);
                        setIsSelected(null);
                      }}
                      sx={{
                        fontSize: "0.75rem",
                        ".css-3k008v-MuiButtonBase-root-MuiTab-root": {
                          borderRadius: "4px !important", 
                          background: "inherit",
                          padding: "10px 15px",
                          color: "#fff !important",
                        },
                      }}
                    />
                    <Tab
                      label="Metric Only"
                      onClick={() => setKpiId(isSelected)}
                      sx={{
                        fontSize: "0.75rem",
                        ".css-3k008v-MuiButtonBase-root-MuiTab-root": {
                          borderRadius: "4px !important",
                          background: "inherit", 
                          padding: "10px 15px",
                          color: "#fff !important",
                        },
                      }}
                    />
                  </Tabs>
                </SoftBox>
              </Grid>
            </Grid>
            {kpis && (
              <SoftBox 
                sx={{ 
                  width: "100%",
                  overflowX: "auto",
                  display: "flex",
                  flexDirection: "row",
                  "&::-webkit-scrollbar": {
                    display: "none"
                  },
                  "-ms-overflow-style": "none",
                  "scrollbarWidth": "none"
                }}
              >
                {kpis?.map((kpi) => (
                  <SoftBox 
                    key={kpi._id} 
                    sx={{ 
                      minWidth: isSmallScreen ? "45%" : "20%",
                      paddingLeft: isSmallScreen ? "0.25rem" : "0.5rem",
                      paddingRight: isSmallScreen ? "0.25rem" : "0.5rem",
                    }}
                  >
                    <ItemBox
                      kpi={kpi}
                      amount={kpi.amount}
                      points={kpi.points}
                      isTimeline={true}
                      isSelected={isSelected}
                      onClick={() => {
                        setShowAll(1);
                        setKpiId(kpi._id);
                        setIsSelected(kpi._id);
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                    />
                    {/* <SoftButton
                      key={kpi._id}
                      variant="outlined"
                      sx={{
                        width: "100%",
                        maxWidth: {
                          mini: "120px",
                          xs: "160px",
                          sm: "200px",
                          md: "240px",
                          lg: "300px",
                        },
                        fontSize: { mini: "10px", sm: "14px", lg: "20px" },
                        color:
                          kpiId === kpi._id
                            ? "white !important"
                            : "#343A40 !important",
                        borderColor: "#E9ECEF",
                        textTransform: "none",
                        padding: {
                          mini: "0.25rem 0.5rem",
                          sm: "0.35rem 0.75rem",
                          lg: "0.5rem 1.25rem",
                        },
                        boxShadow:
                          "0px 2.68548px 4.47581px 0px rgba(0, 0, 0, 0.06)",
                        justifyContent: "start",
                        textAlign: "start",
                        background:
                          kpiId === kpi._id
                            ? primaryColor
                            : "white !important",
                        "&:hover": {
                          borderColor: "#E9ECEF",
                          background: primaryColor,
                        },
                        "&:focus:not(:hover)": {
                          borderColor: "#E9ECEF",
                          background: primaryColor,
                        },
                      }}
                      onClick={() => setKpiId(kpi._id)}
                    >
                      {kpi.title}
                    </SoftButton> */}
                  </SoftBox>
                ))}
              </SoftBox>
            )}
          </SoftBox>
          <Grid container spacing={2}>
            <Grid item mini={12} xs={12} sm={12} xxl={8}>
              <SoftBox
                id="timeline"
                sx={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  position: "relative",
                }}
              >
                {timelineListContent}

                <Modal
                  open={openNotes}
                  onClose={handleCloseNotes}
                  BackdropComponent={Backdrop}
                  BackdropProps={{
                    timeout: 300,
                    sx: {
                      // Same glass scrim used across the app's modals (see assets/theme/components/dialog).
                      backgroundColor: "rgba(15, 23, 42, 0.16)",
                      backdropFilter: "blur(3px)",
                      WebkitBackdropFilter: "blur(3px)",
                    },
                  }}
                >
                  <Fade in={openNotes}>
                    <SoftBox sx={{
                      position: 'fixed',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}>
                      <Notes
                        eKpidId={noteKpiId}
                        onAddNote={handleNoteCount}
                        handleCloseNotes={handleCloseNotes}
                        handleClickAway={(isNote) => {
                          if (!isNote) {
                            handleCloseNotes();
                          }
                        }}
                      />
                    </SoftBox>
                  </Fade>
                </Modal>
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </>
  );
};

export default Timeline;
